import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import type { StickerStatus, SearchResult, UserProfile } from '../types';

function MiniRating({ avg, count }: { avg?: number; count?: number }) {
  if (!avg || !count) return <span className="text-xs text-gray-300 font-semibold">Sin calificaciones</span>;
  const stars = Math.round(avg);
  return (
    <span className="flex items-center gap-1">
      {[1,2,3,4,5].map((s) => (
        <span key={s} className={`text-[10px] ${s <= stars ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
      ))}
      <span className="text-xs text-gray-400 font-semibold">{avg.toFixed(1)} ({count})</span>
    </span>
  );
}

const statusOptions: Array<{ value: StickerStatus; label: string }> = [
  { value: 'DUPLICADA', label: 'Duplicada (para intercambiar)' },
  { value: 'TENGO',     label: 'Tengo' },
  { value: 'FALTA',     label: 'Falta (busca figurita)' },
];

export default function SearchPage() {
  const [stickerId, setStickerId] = useState('');
  const [status, setStatus] = useState<StickerStatus>('DUPLICADA');
  const [submitted, setSubmitted] = useState(false);
  const [soloMiPais, setSoloMiPais] = useState(true);

  const { data: profile } = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data } = await api.get('/profile');
      return data.data;
    },
    retry: false,
  });

  const hasPais = Boolean(profile?.pais);

  const { data, isLoading, error } = useQuery<SearchResult[]>({
    queryKey: ['search', stickerId, status, soloMiPais],
    queryFn: async () => {
      const params: Record<string, string> = { stickerId, status };
      if (!soloMiPais || !hasPais) params['allCountries'] = 'true';
      const { data } = await api.get('/search', { params });
      return data.data?.users ?? [];
    },
    enabled: submitted && stickerId.trim() !== '',
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Page header */}
      <div className="bg-white rounded-2xl p-5 shadow-card border border-amber-100">
        <h1 className="text-2xl font-display text-night mb-1" style={{ fontFamily: '"Fredoka One", cursive' }}>
          Buscar figurita
        </h1>
        <p className="text-sm text-gray-400 font-semibold">
          Encuentra quién tiene la figurita que te falta (o quién la necesita)
        </p>
      </div>

      {/* Search form */}
      <div className="bg-white rounded-2xl p-5 shadow-card border border-amber-100">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-36">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                Número de figurita
              </label>
              <input
                value={stickerId}
                onChange={(e) => { setStickerId(e.target.value); setSubmitted(false); }}
                placeholder="ej: 42"
                required
                className="input-field"
              />
            </div>
            <div className="flex-1 min-w-48">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                Buscar usuarios que tienen estado
              </label>
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value as StickerStatus); setSubmitted(false); }}
                className="input-field"
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Filtro por país */}
          <div className="flex items-center gap-3 p-3 bg-brand-50 rounded-xl border border-brand-100">
            <span className="text-base">🌎</span>
            <div className="flex-1">
              <p className="text-xs font-bold text-brand-600 uppercase tracking-wide mb-1.5">Buscar en</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setSoloMiPais(true); setSubmitted(false); }}
                  disabled={!hasPais}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                    soloMiPais && hasPais
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed'
                  }`}
                  title={!hasPais ? 'Configura tu país en el perfil primero' : undefined}
                >
                  Solo mi país
                </button>
                <button
                  type="button"
                  onClick={() => { setSoloMiPais(false); setSubmitted(false); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                    !soloMiPais || !hasPais
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-brand-300'
                  }`}
                >
                  Todos los países
                </button>
              </div>
              {!hasPais && (
                <p className="text-xs text-amber-600 font-semibold mt-1.5">
                  Configura tu país en el perfil para filtrar por región
                </p>
              )}
            </div>
          </div>

          <Button type="submit" className="self-start">
            🔍 Buscar
          </Button>
        </form>
      </div>

      {/* Results */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size={32} />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 font-semibold">
          Error al buscar. Intenta de nuevo.
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-3">
          <p className="text-sm font-bold text-gray-500">
            {data.length === 0
              ? 'Sin resultados para ese número y estado'
              : `${data.length} usuario${data.length > 1 ? 's' : ''} encontrado${data.length > 1 ? 's' : ''}`}
          </p>

          {data.map((item) => (
            <div
              key={item.userId + item.stickerId}
              className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 flex items-center gap-3"
            >
              {/* Avatar with username initials */}
              <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-600 font-bold text-sm flex items-center justify-center flex-shrink-0 uppercase">
                {item.username.slice(0, 2)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-gray-700 truncate">
                  {item.username}{item.ciudad ? <span className="text-gray-400 font-normal"> · {item.ciudad}</span> : null}
                </div>
                <MiniRating avg={item.ratingAvg} count={item.ratingCount} />
                <div className="text-xs text-gray-400 font-semibold mt-0.5">
                  {item.playerName ?? `Figurita #${item.stickerId}`}
                  {item.nationalTeam && <span className="ml-1">· {item.nationalTeam}</span>}
                  <span className="ml-1">· {item.quantity}×</span>
                </div>
              </div>

              <StatusBadge status={item.status} />
            </div>
          ))}
        </div>
      )}

      {!submitted && (
        <div className="text-center py-12 text-gray-300">
          <div className="text-5xl mb-3">🔍</div>
          <p className="font-semibold text-gray-400">Ingresa un número y busca</p>
        </div>
      )}
    </div>
  );
}
